import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoImg from "../../../../assets/logo.png";
import backgroundImg from "../../../../assets/background.png";
import LanguageSwitcher from "../../../../components/LanguageSwitcher";
import "./OssRegistrationLayout.css";
import LoginButton from "../../../../components/layout/LoginButton";

const ACCESS_STORAGE_KEY = "legup_oss_access_context";
const COMPANY_DETAILS_STORAGE_KEY = "legup_oss_company_details";

export default function OssRegistrationLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("oss-registration-layout");

  // Password exists only in React memory and disappears on full refresh.
  const [ossPassword, setOssPassword] = React.useState("");
  const clearOssPassword = React.useCallback(() => setOssPassword(""), []);

  // Scrub credentials that earlier versions may have saved to localStorage.
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem(COMPANY_DETAILS_STORAGE_KEY);
      if (!saved) return;

      const details = JSON.parse(saved);
      if (!details || typeof details !== "object" || Array.isArray(details)) {
        localStorage.removeItem(COMPANY_DETAILS_STORAGE_KEY);
        return;
      }

      const { password, confirm_password, ...safeDetails } = details;
      localStorage.setItem(COMPANY_DETAILS_STORAGE_KEY, JSON.stringify(safeDetails));
    } catch {
      localStorage.removeItem(COMPANY_DETAILS_STORAGE_KEY);
    }
  }, []);

  const accessContext = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(ACCESS_STORAGE_KEY) || "{}");
    } catch {
      return {};
    }
  }, [location.pathname]);

  const hasVerifiedAccessCode = Boolean(
    accessContext?.email &&
    accessContext?.access_code &&
    accessContext?.preview?.ok,
  );

  const steps = [
    { path: "/register/oss/step-1", label: t("layout.steps.request"), isEnabled: true },
    { path: "/register/oss/access-code", label: t("layout.steps.accessCode"), isEnabled: true },
    { path: "/register/oss/company-details", label: t("layout.steps.companyDetails"), isEnabled: hasVerifiedAccessCode },
    { path: "/register/oss/services", label: t("layout.steps.services"), isEnabled: hasVerifiedAccessCode },
    { path: "/register/oss/review-submit", label: t("layout.steps.review"), isEnabled: hasVerifiedAccessCode },
  ];

  function handleStepClick(step) {
    if (!step.isEnabled) return;
    navigate(step.path);
  }

  return (
    <div className="oss-layout-page">
      <div
        className="oss-layout-bg"
        style={{ backgroundImage: `url(${backgroundImg})` }}
      />

      <div className="oss-layout-topbar">
        <img
          src={logoImg}
          alt={t("layout.logoAlt")}
          onClick={() => navigate("/")}
        />
        <div className="oss-layout-topbar__right">
          <LanguageSwitcher />
          <LoginButton />
        </div>
      </div>

      <div className="oss-layout-shell">
        <aside className="oss-layout-sidebar" aria-label={t("layout.sidebarAria")}>
          {steps.map((step, index) => {
            const isActive = location.pathname === step.path;
            return (
              <button
                key={step.path}
                type="button"
                className={[
                  "oss-step",
                  isActive ? "active" : "",
                  !step.isEnabled ? "locked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleStepClick(step)}
                disabled={!step.isEnabled}
                title={!step.isEnabled ? t("layout.lockedStepTooltip") : step.label}
              >
                <span>{index + 1}</span>
                {step.label}
              </button>
            );
          })}
        </aside>

        <main className="oss-layout-content">
          <Outlet context={{ ossPassword, setOssPassword, clearOssPassword }} />
        </main>
      </div>
    </div>
  );
}
